<script>
import AscPlotDataMixin from '../features/flightData/containers/AscPlotDataMixin.vue'
import AscPlotFiltersMixin from '../features/flightData/containers/AscPlotFiltersMixin.vue'
import AscPlottingMixin from './AscPlottingMixin.vue'
import AscMxRecPermissionMixin from './AscMxRecPermissionMixin.vue'

import {
  MAINTENANCE_ISSUES_CONSTS as MIC,
  PLOT_CONSTS as PLC,
  PLOTLY_CONSTS as PLYC,
  ANALYTIC_MODULE_CONSTS as AMC,
} from '@/stores/consts'
import { mapActions, mapState } from 'vuex'

import AppFeatureFlags from '@/utilities/appFeatureFlags'
import {
  createNewPagePlotGivenPlotTypeId,
  getNextHighchartColor,
} from '@/utilities/plotting'
import { COLLINS_PHM_TIER_3 } from '@/utilities/appConsts'

import cloneDeep from 'lodash/cloneDeep'
import { v4 as uuidv4 } from 'uuid'

export default {
  mixins: [
    AscPlotDataMixin,
    AscPlotFiltersMixin,
    AscPlottingMixin,
    AscMxRecPermissionMixin,
  ],
  data() {
    return {
      plotsLoading: false,
      undoPlotRemoval: false,
      showDeleteSupportingPlotConfirmToast: false,
      isPlotDeletionSuccessful: {},
      showDeleteSupportingAttachmentsConfirmToast: false,
      deleteSupportingPlotToastDefinition: [
        {
          mode: 'toast',
          type: 'warning',
          text: 'Plot deleted',
          position: 'bottom',
          dismissible: false,
          hasToastAction: true,
          toastActionLabel: 'Undo',
          autoClose: true,
          icon: 'mdi-delete',
          duration: 8000,
        },
      ],
      pagePlotDialogVisible: false,
      expandedPagePlot: null,
      inViewPagePlot: null,
    }
  },

  computed: {
    ...mapState(MIC.NAMESPACE, [
      'analyticPagePlots',
      'analyticPlot6MonthsFromDate',
      'analyticPlot30DaysFromDate',
      'analyticPlotToDate',
    ]),
    ...mapState(PLYC.NAMESPACE, ['plotlyTokens']),
    getChartData() {
      return this.chartData // From PlotsDataMixin
    },

    arePlotsEditable() {
      return (
        AppFeatureFlags.getMxRecPageEditPlots() &&
        this.isEditPermissionInvestigate &&
        !this.isOnRecommendationTab
      )
    },

    isTier3Updater() {
      return (
        this.airlineTierTypeId === COLLINS_PHM_TIER_3 &&
        this.isMxRecEditPermission
      )
    },
  },

  watch: {
    pagePlots: {
      deep: true,
      handler(pagePlots) {
        this.pagePlots = pagePlots
      },
    },
  },

  methods: {
    ...mapActions(MIC.NAMESPACE, [
      MIC.GET_MAINTENANCE_ISSUE_PLOTS,
      MIC.UPDATE_PLOT_VISIBILITY,
      MIC.REMOVE_DELETED_PLOT_FROM_SELECTIONS,
      MIC.UPDATE_PLOT_TO_ISSUE_ID_LINK,
      MIC.DELETE_PLOT_LINK,
    ]),
    ...mapActions(PLC.NAMESPACE, [PLC.SAVE_PLOT]),
    ...mapActions(PLYC.NAMESPACE, [PLYC.GET_PLOTLY_TOKEN]),
    ...mapActions(AMC.NAMESPACE, [
      AMC.SET_AMC_PAGE_PLOT_FEATURES_WITH_SELECTOR,
    ]),

    // Shared Methods --------------------------------------------------
    isEyeballVisible(
      isRiskScore = false,
      isIssueInvestigator,
      isFeatureInvestigator,
    ) {
      // eyeball is only visible
      // If the user is a isIssueInvestigator, isFeatureInvestigator and
      // if the plot is a risk score plot and
      // if the plot has no privateRiskScoreIds and
      // has atleast one publicRiskScoreId

      // Probably we need to add Issue reader too here rather than checking the same in supportinfo wrapper
      if (isIssueInvestigator && isFeatureInvestigator) {
        if (isRiskScore) {
          return true
        }
      }

      // defaults to not visible
      return false
    },

    // Pre-Populated Plots Starts ---------------------------------------
    isValidFeatureId(isFeatureInvestigator, publicFeatureIds, featureId) {
      return isFeatureInvestigator
        ? isFeatureInvestigator
        : publicFeatureIds.includes(featureId)
    },

    getPrepopulatedPlotTitle({ title }) {
      return `${title || 'N/A'} ${
        this.isEditPermissionInvestigate || this.isReadOnlyPermissionInvestigate
          ? '(Pre-populated)'
          : ''
      }`
    },

    // Generate Pre-Populated **Analytic Trends Plots**
    createPrepopulatedAnalyticTrendsPagePlot(
      order,
      isFeatureInvestigator,
      isIssueInvestigator,
      {
        caption,
        isRiskScore,
        tailId,
        analyticModuleId,
        publicRiskScoreIds,
        featureIds,
        publicFeatureIds,
      },
      analytic6MonthsDate,
      analyticToDate,
    ) {
      // newPagePlot
      const newPagePlot = cloneDeep(createNewPagePlotGivenPlotTypeId(9151))
      newPagePlot.title = isRiskScore
        ? 'Analytic Risk Score Trends Plot'
        : 'Analytic Feature Trends Plot'

      newPagePlot.order = order
      newPagePlot.caption = caption || ''
      newPagePlot.prepopulated = true
      newPagePlot.plotVisible = false
      newPagePlot.publicRiskScoreIds = publicRiskScoreIds || []
      newPagePlot.isRiskScorePlot = isRiskScore || false
      newPagePlot.isFeaturePlot = !isRiskScore || false
      newPagePlot.isEyeballVisible = this.isEyeballVisible(
        isRiskScore,
        isIssueInvestigator,
        isFeatureInvestigator,
      )
      newPagePlot.isExternallyVisible =
        this.isExternallyVisible(publicRiskScoreIds)
      newPagePlot.featureLockedItems = featureIds || []
      newPagePlot.tailLockedItems = [tailId] || []

      // analyticModule ------------------------------
      const analyticModule = this.filterLookups?.find(
        ({ id }) => id === 'amcAnalyticModule',
      )

      if (analyticModule) {
        const newPagePlotTabs = newPagePlot.options.data.tabs
        const ids = isRiskScore ? publicRiskScoreIds : publicFeatureIds

        featureIds
          .filter(featureId =>
            this.isValidFeatureId(
              isFeatureInvestigator,
              ids?.map(i => i.value),
              featureId,
            ),
          )
          .forEach((featureId, index) => {
            if (index > 0) {
              const colors = newPagePlotTabs.map(item => item.iconColor)
              const duped = cloneDeep(newPagePlotTabs[0])
              const updatedTab = {
                ...duped,
                id: uuidv4(),
                hidden: false,
                tabIndex: newPagePlotTabs.length,
                iconColor: getNextHighchartColor(colors),
                tabDisabled: true,
              }
              newPagePlotTabs.push(updatedTab)
            }

            // amcAnalyticModuleField -----------------------
            const analyticModuleSelectedItems = analyticModule?.items.filter(
              ({ value }) => value === analyticModuleId,
            )
            const amcAnalyticModuleFieldSelection =
              newPagePlotTabs[index].selection[0]
            amcAnalyticModuleFieldSelection.selectorDisabled = true
            amcAnalyticModuleFieldSelection.selection = [analyticModuleId]
            amcAnalyticModuleFieldSelection.selectedItems =
              analyticModuleSelectedItems

            // amcFeatureField -----------------------
            const amcFeatureFieldSelection = newPagePlotTabs[index].selection[1]
            const feature = this.filterLookups.find(
              ({ id }) => id === 'amcFeature',
            )
            const featureSelectedItems = feature?.items.filter(
              ({ value }) => value === featureId,
            )
            // amcFeatureFieldSelection.lockedItems = newPagePlot.featureLockedItems
            amcFeatureFieldSelection.selection = [featureId]
            amcFeatureFieldSelection.selectedItems = featureSelectedItems

            // tailField -----------------------
            const tailFieldSelection = newPagePlotTabs[index].selection[2]
            const tail = this.filterLookups.find(({ id }) => id === 'tail')
            const tailSelectedItems = tail?.items.filter(
              ({ value }) => value === tailId,
            )

            tailFieldSelection.lockedItems = newPagePlot.tailLockedItems
            tailFieldSelection.selection = [tailId]
            tailFieldSelection.selectedItems = tailSelectedItems

            // datesField -----------------------
            const datesFieldSelection = newPagePlotTabs[index].selection[3]
            datesFieldSelection.selectorDisabled = true
            datesFieldSelection.visible = false
            datesFieldSelection.selection.dates = {
              value: '6',
              startDate: analytic6MonthsDate,
              endDate: analyticToDate,
            }

            // Set pre-populated flag
            newPagePlotTabs[index].prepopulated = true
          })
      }

      return newPagePlot
    },

    // Generate Pre-Populated **Analytic Comparison Plots**
    createPrepopulatedAnalyticComparisonPagePlot(
      order,
      isFeatureInvestigator,
      isIssueInvestigator,
      {
        caption,
        isRiskScore,
        tailId,
        analyticModuleId,
        publicRiskScoreIds,
        privateRiskScoreIds,
        featureIds,
        publicFeatureIds,
      },
      analytic30DaysDate,
      analyticToDate,
    ) {
      const newPagePlot = cloneDeep(createNewPagePlotGivenPlotTypeId(9124))
      newPagePlot.title = isRiskScore
        ? 'Analytic Risk Score Comparison Plot'
        : 'Analytic Feature Comparison Plot'
      newPagePlot.order = order
      newPagePlot.caption = caption || ''
      newPagePlot.prepopulated = true
      newPagePlot.plotVisible = false
      newPagePlot.publicRiskScoreIds = publicRiskScoreIds || []
      newPagePlot.privateRiskScoreIds = privateRiskScoreIds || []
      newPagePlot.isRiskScorePlot = isRiskScore || false
      newPagePlot.isFeaturePlot = !isRiskScore || false
      newPagePlot.isEyeballVisible = this.isEyeballVisible(
        isRiskScore,
        isIssueInvestigator,
        isFeatureInvestigator,
      )
      newPagePlot.isExternallyVisible =
        this.isExternallyVisible(publicRiskScoreIds)
      newPagePlot.featureLockedItems = featureIds || []
      newPagePlot.tailLockedItems = [tailId] || []

      // analyticModule ------------------------------
      const analyticModule = this.filterLookups.find(
        ({ id }) => id === 'amcAnalyticModule',
      )

      if (analyticModule) {
        const newPagePlotTabs = newPagePlot.options.data.tabs

        // amcAnalyticModuleField -----------------------
        const analyticModuleSelectedItems = analyticModule.items.filter(
          ({ value }) => value === analyticModuleId,
        )
        const amcAnalyticModuleFieldSelection = newPagePlotTabs[0].selection[0]
        amcAnalyticModuleFieldSelection.selectorDisabled = true
        amcAnalyticModuleFieldSelection.selection = [analyticModuleId]
        amcAnalyticModuleFieldSelection.selectedItems =
          analyticModuleSelectedItems

        // amcFeatureField -----------------------
        const feature = this.filterLookups.find(({ id }) => id === 'amcFeature')
        const ids = isRiskScore ? publicRiskScoreIds : publicFeatureIds
        const featureSelectedItems = feature.items
          .filter(({ value }) =>
            this.isValidFeatureId(
              isFeatureInvestigator,
              ids?.map(id => id.value),
              value,
            ),
          )
          .filter(item => featureIds.includes(item.value))
        const amcFeatureFieldSelection = newPagePlotTabs[0].selection[1]
        // amcFeatureFieldSelection.lockedItems = newPagePlot.featureLockedItems
        amcFeatureFieldSelection.selection = featureIds
        amcFeatureFieldSelection.selectedItems = featureSelectedItems

        // tailField -----------------------
        const tail = this.filterLookups.find(item => item.id === 'tail')
        const tailSelectedItems = tail?.items.filter(
          ({ value }) => value === tailId,
        )
        const tailFieldSelection = newPagePlotTabs[0].selection[2]
        tailFieldSelection.lockedItems = newPagePlot.tailLockedItems
        tailFieldSelection.selection = [tailId]
        tailFieldSelection.selectedItems = tailSelectedItems

        // datesField -----------------------
        const datesFieldSelection = newPagePlotTabs[0].selection[3]
        datesFieldSelection.selectorDisabled = true
        datesFieldSelection.visible = false
        datesFieldSelection.selection.dates = {
          value: '6',
          startDate: analytic30DaysDate,
          endDate: analyticToDate,
        }
      }

      return newPagePlot
    },

    // Generate MXREC Pre-Populated Plots
    createPrepopulatedPagePlots(
      plotDefinitions,
      analytic6MonthsDate,
      analytic30DaysDate,
      analyticToDate,
      isFeatureInvestigator,
      isIssueInvestigator,
      isIssueUpdater,
    ) {
      const pps = []
      const isMxRecPagePrepopulatedPlots =
        AppFeatureFlags.getMxRecPagePrepopulatedPlots()
      if (isMxRecPagePrepopulatedPlots) {
        plotDefinitions.forEach((plotDefinition, index) => {
          const plotTypeId = plotDefinition.plotTypeId
          if (plotTypeId === 9151) {
            pps.push(
              this.createPrepopulatedAnalyticTrendsPagePlot(
                index,
                isFeatureInvestigator,
                isIssueInvestigator,
                plotDefinition,
                analytic6MonthsDate,
                analyticToDate,
              ),
            )
          }
          if (plotTypeId === 9124) {
            pps.push(
              this.createPrepopulatedAnalyticComparisonPagePlot(
                index,
                isFeatureInvestigator,
                isIssueInvestigator,
                plotDefinition,
                analytic30DaysDate,
                analyticToDate,
              ),
            )
          }
        })
      }

      return pps.map(plot => {
        return {
          ...plot,
          title: this.getPrepopulatedPlotTitle(plot, isIssueUpdater),
        }
      })
    },

    // Plotting helper methods Start ------------------------
    isExternallyVisible(publicRiskScoreIds) {
      // Selection has happened
      // eyeball is visible if plot has at least 1 public score
      return publicRiskScoreIds.length ? true : false
    },

    upgradeMxRecPagePlot(
      savedPlot,
      isIssueInvestigator,
      isFeatureInvestigator,
      noGlobalFilter = false,
    ) {
      const upgradedPlot = savedPlot
      const plotTypeId = upgradedPlot.plotTypeId
      const tabs = upgradedPlot.options.data.tabs

      // update if analytic plot
      let updatedTabs = tabs.map(tab => {
        return {
          ...tab,
          selection: this.updateAnalyticTabSelection(
            plotTypeId,
            tab.selection,
            noGlobalFilter,
          ), // From plottingMixin
          tabTitleField: this.updateAnalyticTabTitleField(tab.tabTitleField), // From plottingMixin
        }
      })

      // update general tab
      updatedTabs = updatedTabs.map(tab => {
        return {
          ...tab,
          selection: this.updateGeneralTabSelection(tab.selection), // From plottingMixin
        }
      })

      if (this.isMxRecEditPermission && upgradedPlot.title) {
        upgradedPlot.title = upgradedPlot.title
          .replace('(Pre-populated)', '')
          .trim()
      }

      // update tabs
      upgradedPlot.options.data.tabs = updatedTabs

      // initialize props
      upgradedPlot.isExternallyVisible = true
      upgradedPlot.isEyeballVisible = true
      upgradedPlot.isRiskScorePlot = false
      upgradedPlot.isFeaturePlot = false

      // iterate through all tabs and capture feature information
      const riskScoreIds = []
      const featureIds = []
      const publicRiskScoreIds = []
      const privateRiskScoreIds = []
      const tabSelectedItems = []
      const allFeatureIds = this.mappedAmcFeatures
      updatedTabs.forEach(tab => {
        const feature = tab.selection.find(({ id }) => id === 'amcFeature')
        // the selected items property will give the feature iD that we need
        // to compare with mappedAmcFeatures that comes back from the API
        // and we need to update the selected items property to match what
        // the API has...NOT what was saved with the plot.
        // this will ensure that any updates in the analytic admin screen are reflected
        const isTabHidden = tab.hidden
        if (feature) {
          const items = feature.selectedItems
          const isPrivateTab = []

          items.forEach((item, index) => {
            const featureFromAPI = allFeatureIds.find(
              ({ id }) => id === item.id,
            )

            const isPrivateFeature =
              featureFromAPI && !featureFromAPI.isPrivate ? false : true
            isPrivateTab.push(isPrivateFeature)

            if (featureFromAPI) {
              // update the feature with the values from the feature from the API
              items[index].isPrivate = featureFromAPI.isPrivate
              items[index].isRiskScore = featureFromAPI.isRiskScore
              items[index].isUserPlottable = featureFromAPI.isUserPlottable
            }

            if (item.isRiskScore) {
              riskScoreIds.push(item.id)

              if (!item.isPrivate) {
                publicRiskScoreIds.push(item.id)
              } else if (item.isPrivate && !isTabHidden) {
                privateRiskScoreIds.push(item.id)
              }
            }

            if (!item.isRiskScore && !isTabHidden) {
              featureIds.push(item.id)
            }

            tabSelectedItems.push(item)
          }, items)

          // If there are any private features within that tab and if yes
          // If its a tier 3 updater then show all the public ones hidding both private and hidden tabs
          if (isPrivateTab.includes(true) && this.isTier3Updater) {
            tab.hidden = isPrivateTab
          }

          feature.selectedItems = items
        }
      }, updatedTabs)
      // to update the sate.pagePlotFeatures with selected visualization in tab for trends plot
      this[AMC.SET_AMC_PAGE_PLOT_FEATURES_WITH_SELECTOR]({
        plotId: upgradedPlot.id,
        featureData: tabSelectedItems,
      })
      // load feature specific information if there are riskScoreIds or featureIds
      if (riskScoreIds.length || featureIds.length) {
        const isRiskScore =
          riskScoreIds.length && !featureIds.length ? true : false

        // update analytic properties
        upgradedPlot.isFeatureInvestigator = isFeatureInvestigator
        upgradedPlot.isEyeballVisible = this.isEyeballVisible(
          isRiskScore,
          isIssueInvestigator,
          isFeatureInvestigator,
        )
        upgradedPlot.isExternallyVisible =
          this.isExternallyVisible(publicRiskScoreIds)
        upgradedPlot.isRiskScorePlot = isRiskScore
        upgradedPlot.isFeaturePlot = !isRiskScore
      }

      upgradedPlot.prepopulated = upgradedPlot.prepopulated || false
      return upgradedPlot
    },

    // merge saved propulated and autogenerated prepopulated plots
    mergeSavedPrepopulatedPlots(savedPrepopulatedPlots, prepopulatedPlots) {
      if (savedPrepopulatedPlots.length) {
        // If there are savedPrepopulatedPlots exits then send the same else send the prepopulatedPlots
        const mergedPPlots = prepopulatedPlots.map(ppPlot => {
          const savedPlotExists = savedPrepopulatedPlots.find(
            ({ plotTypeId, isFeaturePlot, isRiskScorePlot }) =>
              plotTypeId === ppPlot.plotTypeId &&
              isFeaturePlot === ppPlot.isFeaturePlot &&
              isRiskScorePlot === ppPlot.isRiskScorePlot,
          )

          // If it exists then may need to send the user updated tabs
          // else send prepopulatedPlots defaults
          // update featureLockedItems and tailLockedItems for each tab/each selection
          const tabs = savedPlotExists
            ? savedPlotExists.options.data.tabs
            : ppPlot.options.data.tabs

          const updatedTabs = tabs.map(tab => {
            const tabDisabled = ppPlot.plotTypeId === 9151
            const selections = tab.selection.map(selection => {
              // Lockeditems
              let lockedItems = []
              if (selection.id === 'amcFeature') {
                // lockedItems = ppPlot.featureLockedItems
              }

              if (selection.id === 'tail') {
                lockedItems = ppPlot.tailLockedItems
              }

              return {
                ...selection,
                lockedItems,
              }
            })

            return {
              ...tab,
              selection: selections,
              tabDisabled,
            }
          })

          if (savedPlotExists) {
            const updatedPpPlot = {
              ...savedPlotExists,
              index: ppPlot.index,
              isEyeballVisible: ppPlot.isEyeballVisible,
              order: ppPlot.order,
              caption: savedPlotExists.caption,
            }
            updatedPpPlot.options.data.tabs = updatedTabs
            return updatedPpPlot
          }

          // If no saved plots exists then update the tabs alone which contains
          // featureLockedItems and tailLockedItems
          ppPlot.options.data.tabs = updatedTabs

          return {
            ...ppPlot,
          }
        })

        return mergedPPlots
      } else {
        // If there are no savedPrepopulatedPlots then send all prepopulatedPlots
        return prepopulatedPlots
      }
    },

    async resetPagePlot(plotId) {
      const plotIndex = this.pagePlots.findIndex(({ id }) => id === plotId)

      if (plotIndex !== -1 && this.inViewPagePlot) {
        this.pagePlots.splice(plotIndex, 1, this.inViewPagePlot)
        await this.handleLoadData(
          this.inViewPagePlot.id,
          this.inViewPagePlot.plotTypeId,
          this.inViewPagePlot.options.data.tabs.map(tab => tab.id),
        ) // From plotDataMixin

        this.expandedPagePlot = null
        this.inViewPagePlot = null
      }
    },

    handleDeletePagePlot(plotId) {
      this.chartData = this.chartData.filter(data => data.plotId !== plotId)
      this.pagePlots = this.pagePlots.filter(({ id }) => id !== plotId)
      this.loadingPlotIds = this.loadingPlotIds.filter(id => id !== plotId)
    },

    async handlePlotPreviewAcceptMxRec() {
      const savedPlot = this.getSavedPlotGivenSavedPlotId() // From plottingMixin
      this.tabIndex = 0
      if (savedPlot) {
        const clone = cloneDeep(savedPlot)
        this.pagePlots.unshift(clone)
        await this.handleAutoApplyPagePlot() // From plottingMixin
      }
    },

    // Check if all the feature tabs have a valid selection
    // If any of the tab is valid then the plot is also valid
    isValidPlot(plot) {
      const invalidTabs = plot?.options?.data?.tabs?.filter(({ selection }) => {
        const noSelectionsCount = selection.filter(
          item => item.selection.length === 0,
        )
        return noSelectionsCount.length > 0
      })

      return invalidTabs.length === 0
    },
    // Plotting helper methods End ---------------------------

    // MxRec PagePlots Start ---------------------------

    isPlotVisible(plot) {
      const plots = this.mxRec?.visiblePlots || []
      return plots.includes(plot.id)
    },

    async getPlotly() {
      const plotlyConfig = []
      if (this.isChronicRepeaters) {
        plotlyConfig.push(
          this[PLYC.GET_PLOTLY_TOKEN]({
            data: { maintenanceId: this.issueId },
            type: 'ParetoBarChart',
          }),
        )
        plotlyConfig.push(
          this[PLYC.GET_PLOTLY_TOKEN]({
            data: { maintenanceId: this.issueId },
            type: 'RelatedEvents',
          }),
        )
      } else {
        plotlyConfig.push(
          this[PLYC.GET_PLOTLY_TOKEN]({
            data: { maintenanceId: this.issueId },
            type: 'MxRec',
          }),
        )
      }
      await Promise.all(plotlyConfig)
    },

    async getPlots() {
      this.plotsLoading = true
      const noGlobalFilter = true
      const plotlyLoading = this.getPlotly()
      if (!this.isMxRecEditPermission) {
        // load custom plots
        const isMxRecPageCustomPlots = AppFeatureFlags.getMxRecPageCustomPlots()

        if (isMxRecPageCustomPlots) {
          await this[MIC.GET_MAINTENANCE_ISSUE_PLOTS](this.issueId)
        }

        // upgrade saved plots
        const upgradedPlots = this.mxRec.plots
          .map(plot =>
            this.upgradeMxRecPagePlot(
              plot,
              this.isEditPermissionInvestigate,
              this.isFeatureInvestigator,
              noGlobalFilter,
            ),
          )
          .map(plot => {
            return {
              ...plot,
              isInvalid: !this.isValidPlot(plot),
              plotVisible: this.isPlotVisible(plot),
            }
          })

        // Split out custom plots and prepopulated plots
        const savedCustomPlots = upgradedPlots.filter(
          plot => !plot.prepopulated,
        )
        const savedPrepopulatedPlots = upgradedPlots.filter(
          plot => plot.prepopulated,
        )

        // Sort custom plots
        const customPlots = savedCustomPlots.sort(
          (a, b) =>
            new Date(b.dateCreated).getTime() -
            new Date(a.dateCreated).getTime(),
        )

        // Create autogenerated prepopulated plots
        const apps = this.analyticPagePlots.sort((a, b) => b.order - a.order)
        const ppPlots = this.createPrepopulatedPagePlots(
          apps,
          this.analyticPlot6MonthsFromDate,
          this.analyticPlot30DaysFromDate,
          this.analyticPlotToDate,
          this.isFeatureInvestigator,
          this.isEditPermissionInvestigate,
          this.isMxRecEditPermission,
        ).sort((a, b) => a.order - b.order)

        // Merge saved propulated and autogenerated prepopulated plots
        const updatedPrepopulatedPlots = this.mergeSavedPrepopulatedPlots(
          savedPrepopulatedPlots,
          ppPlots,
        ).map(plot => {
          return {
            ...plot,
            isInvalid: !this.isValidPlot(plot), // From PlottingMixin
          }
        })

        // assign savedPlots
        this.savedPlots = [...customPlots, ...updatedPrepopulatedPlots].filter(
          plot => {
            return this.isMxRecEditPermission
              ? Object.prototype.hasOwnProperty.call(
                  plot,
                  'isExternallyVisible',
                )
                ? plot.isExternallyVisible
                : false
              : true
          },
        )

        // open each mx rec page plot
        for (const plot of this.savedPlots) {
          this.savedPlotId = plot.id
          await this.handlePlotPreviewAcceptMxRec()
        }
      }

      await plotlyLoading
      this.plotsLoading = false
    },

    // MxRec plots exposed methods for components Start ---------------------------
    async handleRemovePlot(plotId) {
      this.undoPlotRemoval = false
      this.showDeleteSupportingPlotConfirmToast = true
      const filteredPlot = this.pagePlots.find(({ id }) => id === plotId)
      if (filteredPlot) {
        // assign plotToDelete
        this.plotToDelete = filteredPlot
        // assign pagePlots to backupPagePlots
        this.backupPagePlots = this.pagePlots
        // remove deleted plot
        this.pagePlots = this.pagePlots.filter(
          ({ id }) => id !== this.plotToDelete.id,
        )
      }
    },

    async handlePlotVisibility(plotOptions) {
      const pagePlot = this.getPagePlotGivenPlotId(plotOptions.plot) // From PlottingMixin
      // Check if the plot is already saved else save before setting visibility
      // If Plot alredy saved then it will have serverId
      if (pagePlot.serverId) {
        await this[MIC.UPDATE_PLOT_VISIBILITY](plotOptions)
      } else {
        // Save plot before adding it to selections
        const plotServerId = await this.handleSavePagePlot(pagePlot.id)
        if (plotServerId) {
          // plotServerId is the updated plot id after successful save
          await this[MIC.UPDATE_PLOT_VISIBILITY]({
            plot: plotServerId,
            selected: plotOptions.selected,
          })
        }
      }
    },

    async handleDeleteSupportingPlotToastClose() {
      this.showDeleteSupportingPlotConfirmToast = false
      if (!this.undoPlotRemoval) {
        this.isPlotDeleting = true

        // remove plot from chartData, pagePlots, and loadingPlotIds
        this.handleDeletePagePlot(this.plotToDelete.id)

        // get the supporting info type for attachments
        const attachmentType = this.supportingInfoSelectionTypes.find(
          ({ name }) => name === 'ATTACHMENTS',
        )

        // need to delete this plot out of the selections for the
        // attachments type if it's really being deleted
        await this[MIC.REMOVE_DELETED_PLOT_FROM_SELECTIONS]({
          issueId: this.issueId,
          type: attachmentType || {},
          plotId: this.plotToDelete.id,
        })

        // delete plot id link to mx rec
        await this[MIC.DELETE_PLOT_LINK]({
          plotId: this.plotToDelete.id,
          issueId: this.issueId,
        })

        this.isPlotDeletionSuccessful = {
          status: true,
          plot: this.plotToDelete.id,
        }

        this.isPlotDeleting = false
      }
    },

    handleUndoDeleteSupportingPlot() {
      this.pagePlots = this.backupPagePlots
      this.undoPlotRemoval = true
      this.showDeleteSupportingPlotConfirmToast = false
    },

    async handleSavePagePlot(plotId) {
      const pagePlot = this.getPagePlotGivenPlotId(plotId) // From PlottingMixin

      // call upgrade plot here
      const upgradedPagePlot = this.upgradeMxRecPagePlot(
        pagePlot,
        this.isEditPermissionInvestigate,
        this.isFeatureInvestigator,
      )

      // check to see if the eyeball is not visible, is it in the list of visible plots Selections(getUserSelections)
      if (
        !upgradedPagePlot.isEyeballVisible &&
        upgradedPagePlot.isExternallyVisible
      ) {
        // if the eyeball isn't visible, remove it from Selections(getUserSelections)
        await this[MIC.UPDATE_PLOT_VISIBILITY]({
          plot: plotId,
          selected: false,
        })
      }

      // if the eyeball is visible, add it in in the current state
      const pagePlotTitle =
        upgradedPagePlot.title.trim().length > 0
          ? upgradedPagePlot.title.trim()
          : 'PLOT NAME'

      upgradedPagePlot.title = pagePlotTitle
      upgradedPagePlot.maintenanceIssueIds = [this.issueId]
      const wasPlotPreviouslySaved = upgradedPagePlot.serverId

      if (pagePlot) {
        const currentTabIndex = upgradedPagePlot.tabIndex
        upgradedPagePlot.tabIndex = 0
        this.isPlotSaving = true
        this.$forceUpdate()
        upgradedPagePlot.dateSaved = new Date()
        const imageSrc = ''
        const savedPagePlot = {
          pagePlot,
          imageSrc,
          mxRecId: this.issueId,
          plotTypeId: 2,
          callGetPlots: false,
        }

        const plotServerId = await this[PLC.SAVE_PLOT](savedPagePlot)

        // don't run logic below if the plot was already saved
        if (!wasPlotPreviouslySaved) {
          this.handleUpdatePagePlotGivenNewServerId(plotId, plotServerId) // From PlottingMixin
          this.handleUpdateChartDataGivenNewServerId(plotId, plotServerId) // From PlottingMixin
          // Update Plot with the current Issue ID
          await this[MIC.UPDATE_PLOT_TO_ISSUE_ID_LINK]({
            plotId: plotServerId,
            issueId: this.issueId,
          })
          // Get all issue plots
          await this[MIC.GET_MAINTENANCE_ISSUE_PLOTS](this.issueId)
        }

        upgradedPagePlot.tabIndex = currentTabIndex

        // If plot is already open and any updates update the inviewPagePlot too
        this.inViewPagePlot = cloneDeep({
          ...this.inViewPagePlot,
          ...upgradedPagePlot,
        })
        setTimeout(() => {
          this.isPlotSaving = false
        }, 500)

        // return the server id on save plot
        return plotServerId
      }
    },

    async handleExpand(pagePlotId) {
      const pagePlot = this.pagePlots.find(({ id }) => id === pagePlotId)
      if (pagePlot) {
        this.expandedPagePlot = pagePlot
        this.inViewPagePlot = cloneDeep(pagePlot)
        this.pagePlotDialogVisible = true
      }
    },

    async loadPlotData(plotId) {
      this.savedPlotId = plotId
      await this.handleAutoApplyPagePlot() // From PlottingMixin
    },

    handlePagePlotDialogClose(plotId, isUpdated = false, isSaved = false) {
      if (isUpdated || isSaved) this.resetPagePlot(plotId)
      this.pagePlotDialogVisible = false
    },

    handleRefreshData(plotId, plotTypeId, event, options) {
      this.handleManualApplyPagePlot(plotId, plotTypeId, event, options) // From PlottingMixin
    },
  },
}
</script>
